/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Specialized Error Boundaries
 *
 * Specialized error boundary components extracted from ErrorBoundary for modularity.
 */

"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, RefreshCw } from "lucide-react";
import { ErrorBoundary } from "../error-boundary";

/**
 * Form Error Boundary
 *
 * Specialized error boundary for form components.
 */
export function FormErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary
      isolationKey="form"
      fallback={({ error: _error, resetError }) => (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <h3 className="font-medium text-destructive">Form Error</h3>
          </div>
          <p className="mt-1 text-sm text-destructive/80">
            There was a problem with the form. Please try again.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={resetError}
            className="mt-3"
          >
            Reset Form
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Feature Error Boundary
 *
 * Specialized error boundary for feature components.
 */
export function FeatureErrorBoundary({
  children,
  featureName,
}: {
  children: React.ReactNode;
  featureName: string;
}) {
  return (
    <ErrorBoundary
      isolationKey={featureName}
      fallback={({ error: _error, resetError }) => (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
          <h3 className="mt-2 font-medium text-destructive">
            {featureName} Unavailable
          </h3>
          <p className="mt-1 text-sm text-destructive/80">
            This feature is temporarily unavailable. Please try again later.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={resetError}
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Dashboard Error Boundary
 *
 * Specialized error boundary for dashboard components.
 */
export function DashboardErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      isolationKey="dashboard"
      fallback={({ error: _error, resetError, eventId }) => (
        <div className="flex min-h-[60vh] items-center justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <CardTitle>Dashboard Error</CardTitle>
              </div>
              <CardDescription>
                Unable to load dashboard data. Please try refreshing the page.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {eventId && (
                <div className="mb-4 rounded-md bg-muted p-2">
                  <p className="text-xs">Error ID: {eventId}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={resetError} className="flex-1">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Refresh Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
